import React, { useContext, useEffect, useState } from 'react';
import {
  CUBOH_MENU_CHILD_MAP,
  MENU_NOTIFICATION_EVENT,
  MENU_NOTIFICATION_TYPE,
} from 'constants/menuManagement';
import { isEmpty } from 'helpers/utils';
import { useMenuManagement } from 'hooks/useMenuManagement';
import { usePusher } from 'hooks/usePusher';

import {
  Anchor,
  ButtonGroup,
  Card,
  Center,
  Group,
  Image,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import { AddNewDrawerButton } from 'components/MenuManagement/MenuItemActions/AddNewDrawer/AddNewDrawerButton';
import MenuSearch from 'components/MenuManagement/MenuActions/MenuSearch';

import { ErrorState } from 'components/Errors/ErrorState/ErrorState';
import HalftoneTakeout from 'assets/images/halftone-takeoutBox02.png';

import MenuEdit from './MenuEdit';
import CategoryTree from './CategoryTree/CategoryTree';
import EditByBulkButton from './EditByBulk/EditByBulkButton';
import { MenuItemsNoticeCards } from './MenuItemsNoticeCards';

export function MenuItems({ onNavigateToPhotos = () => {} }) {
  const { menu, itemsFetchKey } = useContext(MenuEdit.Context);
  const [searchText, setSearchText] = useState('');
  const [searchUuid, setSearchUuid] = useState('');
  const [searchLabel, setSearchLabel] = useState('');

  const {
    singleMenu,
    findCubohMenuItemChildren,
    getSingleCubohMenu,
    getMerchantIntegrationsFromState,
    fetchAllOptionsForParentChildSelectors,
  } = useMenuManagement();

  // Hides bulk price editing below: the next sync overwrites any price change (CN-51667).
  const isPosSyncInPlace = !!getMerchantIntegrationsFromState(menu?.merchantId || '')
    ?.isPosSyncInPlace;

  const { channels } = usePusher();

  useEffect(() => {
    (async () => {
      await getSingleCubohMenu(menu?.companyId, menu?.merchantId, menu?.uuid, searchUuid);
      // also fetch all the simple parent/child options for the menu
      // this does not affect general loading or navigation of the menu
      // only the loading of the parent/child options in the drawer
      fetchAllOptionsForParentChildSelectors(menu);
    })();
  }, [menu?.companyId, menu?.merchantId, searchUuid, itemsFetchKey]);

  useEffect(() => {
    if (!menu?.companyId || !channels) return undefined;

    const channel = channels.find(
      ch => ch.name === `${import.meta.env.VITE_WEB_SOCKET_STAGE}-company-${menu.companyId}`,
    );

    if (!channel) return undefined;

    const handleUpdate = () => {
      getSingleCubohMenu(menu.companyId, menu.merchantId, menu.uuid, searchUuid);
      fetchAllOptionsForParentChildSelectors(menu);
    };

    const handleMenuUpdate = data => {
      if (data?.message && (!data.menuUuid || data.menuUuid === menu?.uuid)) {
        handleUpdate();
      }
    };

    channel.bind('menu-update', handleMenuUpdate);

    return () => {
      channel.unbind('menu-update', handleMenuUpdate);
    };
  }, [channels, menu?.companyId, menu?.merchantId, menu?.uuid, searchUuid]);

  // Clear the active search when any add/edit/delete succeeds — otherwise the
  // newly affected item shows up in the search results, which is confusing.
  useEffect(() => {
    if (!searchUuid) return undefined;
    const handleNotification = event => {
      if (event.detail.type === MENU_NOTIFICATION_TYPE.SUCCESS) {
        setSearchUuid('');
        setSearchLabel('');
        setSearchText('');
      }
    };
    window.addEventListener(MENU_NOTIFICATION_EVENT, handleNotification);
    return () => window.removeEventListener(MENU_NOTIFICATION_EVENT, handleNotification);
  }, [searchUuid]);

  if (singleMenu?.error?.hasError) {
    return <ErrorState />;
  }

  if (
    singleMenu?.isLoading ||
    Object.keys(singleMenu?.data ?? {}).length === 0 ||
    !singleMenu.data[menu?.merchantId]
  ) {
    return <LoadingOverlay visible />;
  }

  const setFilterTerm = eventOrString => {
    setSearchText(eventOrString?.target?.value || eventOrString);
  };

  const handleResetSearch = () => {
    setSearchUuid('');
    setSearchLabel('');
    setSearchText('');
  };

  const menuData = singleMenu?.data[menu?.merchantId][0];
  const categories = findCubohMenuItemChildren({
    parentUuid: menuData?.uuid,
    childItemType: CUBOH_MENU_CHILD_MAP[menuData?.itemType],
  });
  const menuHasNoItems = isEmpty(categories);

  const emptyMenu = menuHasNoItems;

  return (
    <Stack gap="xl" mb="md">
      <MenuItemsNoticeCards onNavigateToPhotos={onNavigateToPhotos} />
      <Card p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <ButtonGroup>
              <AddNewDrawerButton parentRecord={menu} />
              {!isPosSyncInPlace && (
                <EditByBulkButton
                  disabled={emptyMenu || menu?.clonedMenu}
                  isCloned={menu?.clonedMenu}
                  companyId={menu?.companyId}
                  merchantId={menu?.merchantId}
                  menuUuid={menu?.uuid}
                />
              )}
            </ButtonGroup>
            <MenuSearch
              searchTerm={searchText}
              menuUuid={menu?.uuid}
              setSearchUuid={setSearchUuid}
              setFilterTerm={setFilterTerm}
              companyId={menu?.companyId}
              merchantId={menu?.merchantId}
              setSearchLabel={setSearchLabel}
            />
          </Group>
          {searchLabel && (
            <Group gap="xs3">
              <Text variant="sublabel-sm">Results for &ldquo;{searchLabel}&rdquo;.</Text>
              <Anchor
                component="button"
                variant="sublabel"
                size="sm"
                underline="always"
                onClick={handleResetSearch}
              >
                Reset
              </Anchor>
            </Group>
          )}
          {emptyMenu ? (
            <Center py="xl">
              <Stack align="center">
                <Image src={HalftoneTakeout} w="var(--dimension-xl5)" />
                <Text variant="body-md" c="var(--content-primary)">
                  This menu has no items. Please create one.
                </Text>
              </Stack>
            </Center>
          ) : (
            <Stack gap="md">
              {categories.map((category, index) => (
                <CategoryTree
                  key={category.uuid}
                  category={category}
                  order={index + 1}
                  searchUuid={searchUuid}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
